import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { pickLang } from "@/data/stoicosCards";
import { SVF_PAGE, PHILOSOPHERS } from "@/data/ancientLibrary";
import { entriesForPhilosopher } from "@/data/svfEntries";
import { AboutEditionNote, AlBreadcrumb, useAlLabels } from "@/pages/ancient/common";

export default function SvfCollectionPage() {
  const { lang, labels } = useAlLabels();

  useSEO({
    title: "Stoicorum Veterum Fragmenta (SVF) — Fragments of the Early Stoics | StoicOs.ai",
    description: "The SVF collection in the StoicOs Ancient Library: fragments and testimonia of Zeno, Cleanthes and Chrysippus, assembled by Hans von Arnim — with Greek/Latin sources, translations and commentary.",
    canonical: "https://stoicos.ai/ancient-library/svf",
    jsonLd: [
      webPage({ name: "Stoicorum Veterum Fragmenta (SVF)", description: "Fragments and testimonia of the early Stoics.", url: "/ancient-library/svf" }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "Ancient Library", url: "/ancient-library" }, { name: "SVF", url: "/ancient-library/svf" }]),
    ],
  });

  return (
    <main data-testid="svf-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <AlBreadcrumb items={[{ to: "/", label: "Home" }, { to: "/ancient-library", label: labels.library }, { label: "SVF" }]} />
        <h1 data-testid="svf-title" className="font-serif text-4xl sm:text-5xl text-bone tracking-tight leading-[1.08]">{SVF_PAGE.title}</h1>
        <p className="mt-6 font-serif italic text-xl md:text-2xl text-gold/90">{pickLang(SVF_PAGE.subtitle, lang)}</p>
        {SVF_PAGE.about.map((p, i) => (
          <p key={i} className="mt-8 text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(p, lang)}</p>
        ))}

        <section className="mt-16">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.collections}</h2>
          <div className="space-y-6">
            {PHILOSOPHERS.map((p) => {
              const count = entriesForPhilosopher(p.slug).filter((e) => e.verified).length;
              const inner = (
                <>
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3 className="font-serif text-2xl text-bone tracking-tight">{pickLang(p.name, lang)}</h3>
                    {p.greekName && <span className="text-gold/80 text-lg" style={{ fontFamily: '"EB Garamond", Georgia, "Times New Roman", serif' }}>{p.greekName}</span>}
                    <span className="text-[11px] tracking-[0.18em] uppercase text-fog/60">{p.dates}</span>
                  </div>
                  <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-gold/80">{labels.volume} {p.svfVolume} · {p.available ? `${labels.available} — ${count} ${labels.entries.toLowerCase()}` : labels.inPreparation}</p>
                  <p className="mt-3 text-bone/75 font-reading text-base leading-[1.8]">{pickLang(p.blurb, lang)}</p>
                </>
              );
              return p.available ? (
                <Link key={p.slug} to={`/ancient-library/svf/${p.slug}`} data-testid={`svf-philosopher-${p.slug}`} className="group block p-7 md:p-8 border border-gold/25 bg-ink-elevated/40 hover:border-gold/50 transition-colors">{inner}</Link>
              ) : (
                <div key={p.slug} data-testid={`svf-philosopher-${p.slug}`} className="p-7 md:p-8 border border-white/8 bg-ink-elevated/20 opacity-75">{inner}</div>
              );
            })}
          </div>
        </section>

        <AboutEditionNote />
      </div>
    </main>
  );
}
